import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InputField } from "components/designV2/inputfield/inputfield";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type {
  SalaryAdjustment,
  SalaryAdjustmentDatabaseRowKey,
} from "./adjustSalary.logic";
import type { SalaryAdjustmentEditState } from "./adjustSalaryModal.hook";
import { salaryAdjustmentPresentation } from "./adjustSalaryModal.presentation";
import {
  AdjustmentActions,
  AdjustmentHeader,
  AdjustmentInlineInputWrap,
  AdjustmentList,
  AdjustmentListCell,
  AdjustmentListRow,
  AdjustmentsSection,
  AdjustmentsTitle,
  AmountCell,
  AmountSign,
  IconActionButton,
} from "./adjustSalaryModal.styled";

interface Props {
  readonly adjustments: readonly SalaryAdjustment[];
  readonly editState: SalaryAdjustmentEditState;
  readonly isEditingAmountValid: boolean;
  readonly isSubmitting: boolean;
  readonly onCancelEdit: () => void;
  readonly onConfirmEdit: () => void;
  readonly onEdit: (adjustment: SalaryAdjustment) => void;
  readonly onEditingAmountChange: (value: string) => void;
  readonly onRemove: (key: SalaryAdjustmentDatabaseRowKey) => void;
}

interface RowProps extends Omit<Props, "adjustments"> {
  readonly adjustment: SalaryAdjustment;
  readonly amountFormatter: Intl.NumberFormat;
}

const AdjustmentRow = (props: RowProps): JSX.Element => {
  const { t } = useTranslation();
  const presentation = salaryAdjustmentPresentation[props.adjustment.key];
  const adjustmentName = t(presentation.labelTranslationKey);
  const editingValue =
    props.editState.type === "editing" &&
    props.editState.key === props.adjustment.key
      ? props.editState.value
      : undefined;
  const isEditing = editingValue !== undefined;

  return (
    <AdjustmentListRow>
      <AdjustmentListCell>{adjustmentName}</AdjustmentListCell>
      <AmountCell>
        {isEditing ? (
          <AdjustmentInlineInputWrap>
            {presentation.fixedSign && (
              <AmountSign>{presentation.fixedSign}</AmountSign>
            )}
            <InputField
              ariaLabel={`${t("Salary.EditAdjustment")} ${adjustmentName}`}
              hasError={editingValue.length > 0 && !props.isEditingAmountValid}
              maxLength={presentation.inputMaxLength}
              size="compact"
              type="text"
              value={editingValue}
              onChange={props.onEditingAmountChange}
            />
          </AdjustmentInlineInputWrap>
        ) : (
          <>
            {props.amountFormatter.format(props.adjustment.value)}{" "}
            {t("Salary.Kr")}
          </>
        )}
      </AmountCell>
      <AdjustmentActions>
        {isEditing ? (
          <EditActions
            adjustmentName={adjustmentName}
            isEditingAmountValid={props.isEditingAmountValid}
            isSubmitting={props.isSubmitting}
            onCancel={props.onCancelEdit}
            onConfirm={props.onConfirmEdit}
          />
        ) : (
          <DefaultActions
            adjustment={props.adjustment}
            adjustmentName={adjustmentName}
            isDisabled={
              props.isSubmitting || props.editState.type === "editing"
            }
            onEdit={props.onEdit}
            onRemove={props.onRemove}
          />
        )}
      </AdjustmentActions>
    </AdjustmentListRow>
  );
};

interface EditActionsProps {
  readonly adjustmentName: string;
  readonly isEditingAmountValid: boolean;
  readonly isSubmitting: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

const EditActions = (props: EditActionsProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <>
      <IconActionButton
        aria-label={`${t("Salary.UpdateAdjustment")} ${props.adjustmentName}`}
        disabled={!props.isEditingAmountValid || props.isSubmitting}
        type="button"
        onClick={props.onConfirm}
      >
        <CustomIcon color={ColorV2.Blue1} name="v2/checkmark" size="20px" />
      </IconActionButton>
      <IconActionButton
        aria-label={`${t("Salary.Cancel")} ${props.adjustmentName}`}
        disabled={props.isSubmitting}
        type="button"
        onClick={props.onCancel}
      >
        <CustomIcon color={ColorV2.Blue1} name="v2/cross" size="20px" />
      </IconActionButton>
    </>
  );
};

interface DefaultActionsProps {
  readonly adjustment: SalaryAdjustment;
  readonly adjustmentName: string;
  readonly isDisabled: boolean;
  readonly onEdit: (adjustment: SalaryAdjustment) => void;
  readonly onRemove: (key: SalaryAdjustmentDatabaseRowKey) => void;
}

const DefaultActions = (props: DefaultActionsProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <>
      <IconActionButton
        aria-label={`${t("Salary.EditAdjustment")} ${props.adjustmentName}`}
        disabled={props.isDisabled}
        type="button"
        onClick={() => props.onEdit(props.adjustment)}
      >
        <CustomIcon color={ColorV2.Blue1} name="v2/pen" size="20px" />
      </IconActionButton>
      <IconActionButton
        aria-label={`${t("Salary.RemoveAdjustment")} ${props.adjustmentName}`}
        disabled={props.isDisabled}
        type="button"
        onClick={() => props.onRemove(props.adjustment.key)}
      >
        <CustomIcon color={ColorV2.Blue1} name="v2/trash" size="18px" />
      </IconActionButton>
    </>
  );
};

export const AdjustSalaryModalList = (props: Props): JSX.Element | null => {
  const { i18n, t } = useTranslation();
  const amountFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { signDisplay: "always" }),
    [i18n.language],
  );
  if (props.adjustments.length === 0) return null;

  return (
    <AdjustmentsSection>
      <AdjustmentsTitle>{t("Salary.CurrentAdjustments")}</AdjustmentsTitle>
      <AdjustmentList>
        <AdjustmentHeader>
          <AdjustmentListCell>{t("Salary.Name")}</AdjustmentListCell>
          <AmountCell>{t("Salary.Amount")}</AmountCell>
          <AdjustmentListCell aria-hidden="true" />
        </AdjustmentHeader>
        {props.adjustments.map((adjustment) => (
          <AdjustmentRow
            key={adjustment.key}
            adjustment={adjustment}
            amountFormatter={amountFormatter}
            editState={props.editState}
            isEditingAmountValid={props.isEditingAmountValid}
            isSubmitting={props.isSubmitting}
            onCancelEdit={props.onCancelEdit}
            onConfirmEdit={props.onConfirmEdit}
            onEdit={props.onEdit}
            onEditingAmountChange={props.onEditingAmountChange}
            onRemove={props.onRemove}
          />
        ))}
      </AdjustmentList>
    </AdjustmentsSection>
  );
};
